import { MutationCache, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';
import { Suspense, type PropsWithChildren, useState } from 'react';
import i18n from '@/i18n';
import { toast } from '@/shared/lib/toast';
import { getIsDemoUser, isRlsDeniedError } from '@/shared/lib/demoGuard';
import { reportClientError } from '@/shared/lib/reportError';

/**
 * §10.2: caché offline. Persistimos react-query en IndexedDB para que la
 * app abra con los últimos datos vistos aunque no haya red. Los datos
 * se refetchan automáticamente cuando vuelve la conexión.
 *
 * gcTime alto (24h) para que la caché no se limpie prematuramente; combinado
 * con staleTime bajo (30s) los datos se muestran instantáneos y se refrescan
 * en background.
 */
const queryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 24 * 60 * 60_000, // 24 h
      retry: (failureCount: number, _err: unknown) => failureCount < 2,
      // Red de seguridad: al volver a la app (o reconectar) se refresca lo que
      // esté "stale" (>30s). Complementa la invalidación explícita por mutación
      // para que la UI no muestre datos viejos tras un rato en segundo plano.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // offlineFirst: si hay datos en cache los devuelve, y encola el fetch
      // para cuando vuelva la conexión. Sin esto las queries "quedan" en
      // pending indefinidamente offline en lugar de servir cache.
      networkMode: 'offlineFirst' as const,
    },
    mutations: {
      // Las mutations offline se pausan (paused) y se reintentan
      // automáticamente cuando vuelva la conexión. React Query maneja
      // el reintento sin duplicar.
      networkMode: 'offlineFirst' as const,
      retry: 1,
    },
  },
};

const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key: string) => get(key).then((v) => (v == null ? null : String(v))),
    setItem: (key: string, value: string) => set(key, value),
    removeItem: (key: string) => del(key),
  },
  key: 'MANUSPLIT_QUERY_CACHE_V1',
  throttleTime: 1_000,
});

/**
 * Global mutation error handler: si RLS rechaza una escritura, mostramos
 * un toast amigable. En modo demo el mensaje explica que la cuenta es
 * read-only; en el resto sale un genérico "no tienes permiso" (más útil
 * que el mensaje crudo de Postgres).
 */
const mutationCache = new MutationCache({
  onError: (err) => {
    if (!isRlsDeniedError(err)) {
      // Errores de mutación NO relacionados con permisos (p. ej. una validación
      // que revienta, un constraint de BD): se registran para diagnóstico.
      reportClientError({
        source: 'mutation',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? (err.stack ?? null) : null,
      });
      return;
    }
    const t = i18n.t.bind(i18n);
    if (getIsDemoUser()) {
      toast.warning(t('common.demo_readonly_title'), t('common.demo_readonly_body'));
    } else {
      toast.error(t('common.permission_denied_title'), t('common.permission_denied_body'));
    }
  },
});

export function Providers({ children }: PropsWithChildren) {
  const [queryClient] = useState(
    () => new QueryClient({ ...queryClientConfig, mutationCache })
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60_000,
        // Solo persistir queries con datos exitosos; ignorar errores y queries vacías
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => query.state.status === 'success',
        },
        // Invalidar la caché persistida si cambia el buster (ej. tras breaking
        // change).
        //
        // **`v2` desde el 2026-08-29, y el motivo es el caso de manual.** Los
        // perfiles guardados antes de ese día los escribió el código en el que
        // `participantSchema` no declaraba `gdpr_consent_at`, así que Zod lo
        // descartaba y en IndexedDB quedó un perfil **sin el campo**. Al
        // arrancar, la caché se rehidrata antes de que responda el servidor: el
        // `ConsentGate` veía un perfil sin consentimiento, pintaba la puerta, y
        // un segundo después la consulta de verdad la hacía desaparecer. Un
        // diálogo modal que parpadea en cada recarga.
        //
        // Que la forma de un esquema cambie **es** un breaking change para lo
        // que ya está guardado, aunque el tipo compile: la caché no se vuelve a
        // parsear al rehidratar, se usa tal cual quedó escrita. De ahí que la
        // regla sea tocar esto al cambiar un esquema que se persista, y no sólo
        // al cambiar la forma de una clave de consulta.
        buster: 'v2',
      }}
    >
      <Suspense fallback={null}>{children}</Suspense>
    </PersistQueryClientProvider>
  );
}
